import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { notificationService } from '../services/firebase/notification.service';
import { Notification, NotificationCount } from '../types/notification';
import { db } from '../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

interface NotificationContextType {
  notifications: Notification[];
  notificationCounts: NotificationCount;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: (category?: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationCounts, setNotificationCounts] = useState<NotificationCount>({});
  const [weddingId, setWeddingId] = useState<string | null>(null);

  // Récupérer le weddingId de l'utilisateur
  useEffect(() => {
    if (!currentUser) {
      setWeddingId(null);
      return;
    }

    // Chercher le weddingId associé à l'utilisateur
    const fetchWeddingId = async () => {
      try {
        if (currentUser && !currentUser.isAnonymous) {
          // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] Récupération du weddingId pour l\'utilisateur:', currentUser.uid);
          const weddingsQuery = query(
            collection(db, 'weddings'),
            where('userId', '==', currentUser.uid)
          );

          const weddingSnapshot = await getDocs(weddingsQuery);
          if (weddingSnapshot.empty) {
            console.warn('[DEBUG NotificationContext] Aucun mariage trouvé pour l\'utilisateur:', currentUser.uid);
            return;
          }

          const id = weddingSnapshot.docs[0].id;
          // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] WeddingId trouvé:', id);
          setWeddingId(id);
        } else {
          // If user is anonymous, no weddingId is associated in this context
          setWeddingId(null);
        }


      } catch (error) {
        console.error('[DEBUG NotificationContext] Erreur lors de la récupération du weddingId:', error);
      }
    };

    fetchWeddingId();
  }, [currentUser]);

  // Charger les notifications une fois le weddingId récupéré
  useEffect(() => {
    if (!currentUser || !weddingId) {
      setNotifications([]);
      setNotificationCounts({});
      return;
    }

    // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] Chargement des notifications pour le weddingId:', weddingId);

    // Charger les notifications initiales
    const loadNotifications = async () => {
      try {
        const userNotifications = await notificationService.getNotifications(weddingId);
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] Notifications récupérées:', userNotifications);
        setNotifications(userNotifications);
        setNotificationCounts(notificationService.getNotificationCount(userNotifications));
      } catch (error) {
        console.error('[DEBUG NotificationContext] Erreur lors du chargement des notifications:', error);
      }
    };

    loadNotifications();

    // S'abonner aux mises à jour des notifications
    // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] Abonnement aux notifications pour le weddingId:', weddingId);
    const unsubscribe = notificationService.subscribeToNotifications(
      weddingId,
      (updatedNotifications) => {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] Mise à jour des notifications reçue:', updatedNotifications);
        setNotifications(updatedNotifications);
        setNotificationCounts(notificationService.getNotificationCount(updatedNotifications));
      }
    );

    return () => {
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] Désabonnement des notifications');
      unsubscribe();
    };
  }, [currentUser, weddingId]);

  const markAsRead = async (notificationId: string) => {
    try {
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] Marquage de la notification comme lue:', notificationId);
      await notificationService.markAsRead(notificationId);

      // Mettre à jour localement les notifications
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === notificationId
            ? { ...notification, isRead: true }
            : notification
        )
      );
    } catch (error) {
      console.error('[DEBUG NotificationContext] Erreur lors du marquage de la notification:', error);
    }
  };

  const markAllAsRead = async (category?: string) => {
    if (!currentUser || !weddingId) return;

    try {
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG NotificationContext] Marquage de toutes les notifications comme lues');
      await notificationService.markAllAsRead(weddingId, category);

      // Mettre à jour localement les notifications
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          !category || notification.category === category
            ? { ...notification, isRead: true }
            : notification
        )
      );
    } catch (error) {
      console.error('[DEBUG NotificationContext] Erreur lors du marquage de toutes les notifications:', error);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        notificationCounts,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
} 